import type { InteractiveAttempt } from '../core/TaskAttempt';
import type { CoinUpgradeId } from '../data/coinUpgrades';
// Batch state machines for the three Level 1 processes. Pure functions over a
// Batch record so the same code runs attended (Focus Mode), unattended (normal
// clock), headless (npm run sim), and for employee work at day end.
import { CURDLE, CUT, PASTEURIZE, RENNET, RICOTTA, STRETCH, TALEGGIO, YIELD } from '../config/constants';
import { CHEESES } from '../data/cheeses';
import { finalStars } from '../data/pricing';

export type Process = 'mozzarella' | 'ricotta' | 'taleggio';
export type Stage =
  // mozzarella (pasteurize and rennet are shared with taleggio)
  | 'pasteurize'
  | 'curdle'
  | 'rennet'
  | 'cut'
  | 'stretch'
  | 'bag'
  // ricotta
  | 'heat'
  | 'acidify'
  | 'skim'
  | 'drain'
  | 'tub'
  // taleggio
  | 'culture'
  | 'mold'
  | 'brine'
  | 'rack';
export type Station = 'vat' | 'table' | 'tub';
export type Burner = 'off' | 'low' | 'high';
export type PenaltyKind = 'quality' | 'yield';

export const PROCESS_STAGES: Record<Process, Stage[]> = {
  mozzarella: ['pasteurize', 'curdle', 'rennet', 'cut', 'stretch', 'bag'],
  ricotta: ['heat', 'acidify', 'skim', 'drain', 'tub'],
  taleggio: ['pasteurize', 'culture', 'rennet', 'mold', 'brine', 'rack'],
};

export const PROCESS_CHEESE: Record<Process, string> = {
  mozzarella: 'mozzarella_fresh',
  ricotta: 'ricotta',
  taleggio: 'taleggio',
};

export const STAGE_LABEL: Record<Stage, string> = {
  pasteurize: 'Pasteurize',
  curdle: 'Curdle',
  rennet: 'Rennet & Set',
  cut: 'Cut & Drain',
  stretch: 'Heat & Stretch',
  bag: 'Bag',
  heat: 'Heat the whey',
  acidify: 'Acidify & Rest',
  skim: 'Skim',
  drain: 'Drain',
  tub: 'Tub',
  culture: 'Culture & Ripen',
  mold: 'Cut, Mold & Drain',
  brine: 'Brine',
  rack: 'To the Cellar',
};

export interface Penalty {
  minute: number;
  key: string;
  kind: PenaltyKind;
  amount: number;
}

export interface Batch {
  interactiveAttempts?: Record<string, InteractiveAttempt>;
  coinUpgradeSnapshot?: Partial<Record<CoinUpgradeId, number>>;
  ownerId: string;
  ownerName: string;
  matrixActive: boolean;
  process: Process;
  stage: Stage;
  gallons: number;
  cream: number;
  quality: number;
  yieldFactor: number;

  temp: number;
  tempVel: number;
  burner: Burner;
  cooling: boolean;
  heating: boolean;

  // pasteurize (mozzarella, taleggio)
  heldMinutes: number;
  pasteurizeReady: boolean;
  overheldMinutes: number;
  scorchedMinutes: number;

  // curdle (mozzarella)
  acidAdded: boolean;
  acidTemp: number | null;
  curdleStirred: boolean;
  /** Absolute minute when the milk first cooled into the acid band */
  curdleReachedMinute: number | null;
  curdleOvertimeApplied: boolean;

  // rennet (mozzarella, taleggio)
  rennetAdded: boolean;
  rennetStirred: boolean;
  rennetAddedMinute: number | null;

  // cut (mozzarella)
  slices: number;
  drained: boolean;
  firmed: boolean;

  // stretch (mozzarella)
  stretched: boolean;
  formed: boolean;
  overheatApplied: boolean;

  // ricotta
  restStartMinute: number | null;
  skimmed: boolean;
  drainStartMinute: number | null;

  // taleggio
  cultureAdded: boolean;
  cultureTemp: number | null;
  cultureAddedMinute: number | null;
  ripened: boolean;
  moldStartMinute: number | null;
  moldTurns: number[];
  moldDone: boolean;
  brineStartMinute: number | null;

  /** Finished units waiting to be bagged, tubbed, or racked */
  units: number;

  stageStartMinute: number;
  startMinute: number;
  penalties: Penalty[];
  perfectedTasks: string[];
  perfectionWindows: Record<string, number>;
}

export function createBatch(process: Process, gallons: number, quality: number, cream: number, absMinute: number, ownerId = 'player', ownerName = 'Player', matrixActive = false): Batch {
  return {
    process,
    ownerId,
    ownerName,
    matrixActive,
    stage: PROCESS_STAGES[process][0],
    gallons,
    cream,
    quality,
    yieldFactor: 1,
    temp: process === 'ricotta' ? RICOTTA.START_TEMP : PASTEURIZE.START_TEMP,
    tempVel: 0,
    burner: 'off',
    cooling: false,
    heating: false,
    heldMinutes: 0,
    pasteurizeReady: false,
    overheldMinutes: 0,
    scorchedMinutes: 0,
    acidAdded: false,
    acidTemp: null,
    curdleStirred: false,
    curdleReachedMinute: null,
    curdleOvertimeApplied: false,
    rennetAdded: false,
    rennetStirred: false,
    rennetAddedMinute: null,
    slices: 0,
    drained: false,
    firmed: false,
    stretched: false,
    formed: false,
    overheatApplied: false,
    restStartMinute: null,
    skimmed: false,
    drainStartMinute: null,
    cultureAdded: false,
    cultureTemp: null,
    cultureAddedMinute: null,
    ripened: false,
    moldStartMinute: null,
    moldTurns: [],
    moldDone: false,
    brineStartMinute: null,
    units: 0,
    stageStartMinute: absMinute,
    startMinute: absMinute,
    penalties: [],
    perfectedTasks: [],
    perfectionWindows: {},
  };
}

/** Where the batch physically is. */
export function stationFor(b: Batch): Station {
  if (b.stage === 'mold') return 'table';
  if (b.stage === 'brine' || b.stage === 'rack') return 'tub';
  return 'vat';
}

/**
 * Apply a penalty. Drip penalties (per-minute) pass `merge = true` so consecutive
 * hits with the same key collapse into one log line.
 */
export function penalize(b: Batch, minute: number, amount: number, key: string, kind: PenaltyKind = 'quality', merge = false): void {
  if (amount <= 0) return;
  if (kind === 'quality') b.quality = Math.max(1, b.quality - amount);
  else b.yieldFactor = Math.max(YIELD.MIN_FACTOR, b.yieldFactor - amount);
  const last = b.penalties[b.penalties.length - 1];
  if (merge && last && last.key === key && last.kind === kind) {
    last.amount += amount;
    return;
  }
  b.penalties.push({ minute, key, kind, amount });
}

export function formatPenalty(p: Penalty): string {
  return `${p.key} (−${p.amount.toFixed(2)} ${p.kind})`;
}

export function stars(b: Batch): number {
  const n = b.process === 'mozzarella' ? 4 : 0;
  const bonus = b.matrixActive && n > 0 ? b.perfectedTasks.filter((t) => ['pasteurize', 'curdle_stir', 'curd_harden', 'stretch'].includes(t)).length / n : 0;
  return finalStars(b.quality + bonus);
}

/** All milk recipes share this yield model. Ricotta recovers solids from leftover
 * whey, not another equal-input milk batch, so it retains its existing whey yield.
 */
export function projectedPounds(b: Batch): number {
  if (b.process === 'ricotta') return b.gallons * RICOTTA.LB_PER_GAL * b.yieldFactor;
  return b.gallons * YIELD.LB_PER_GAL * (1 + (b.cream - YIELD.CREAM_REF) / YIELD.CREAM_DIV) * b.yieldFactor;
}

export function unitPounds(b: Batch): number {
  return CHEESES[PROCESS_CHEESE[b.process]].unitPounds;
}

export function projectedUnits(b: Batch): number {
  return Math.max(0, Math.floor(projectedPounds(b) / unitPounds(b)));
}

/** Temperature physics with lag, in one-minute substeps for stability. */
function stepTemperature(b: Batch, dt: number): void {
  const idle = b.temp > PASTEURIZE.AMBIENT ? -PASTEURIZE.COOL_RATE_OFF : 0;
  let targetRate: number;
  if (b.stage === 'pasteurize' || b.stage === 'heat') {
    targetRate = b.burner === 'off' ? idle : PASTEURIZE.HEAT_RATE[b.burner];
  } else if (b.stage === 'stretch' && b.heating) {
    targetRate = STRETCH.HEAT_RATE;
  } else if (b.cooling) {
    targetRate = -CURDLE.COOL_RATE;
  } else {
    targetRate = idle;
  }
  b.tempVel += (targetRate - b.tempVel) * Math.min(1, PASTEURIZE.LAG * dt);
  b.temp = Math.max(PASTEURIZE.AMBIENT, b.temp + b.tempVel * dt);
}

/** Advance the batch by `minutes` of game time. `now` is the absolute minute after the advance. */
export function tickBatch(b: Batch, minutes: number, now: number): void {
  if (minutes <= 0) return;
  let remaining = minutes;
  while (remaining > 0) {
    const dt = Math.min(1, remaining);
    remaining -= dt;
    const minuteNow = now - remaining;
    stepTemperature(b, dt);

    switch (b.stage) {
      case 'pasteurize': {
        if (b.temp > PASTEURIZE.SCORCH_TEMP) {
          b.scorchedMinutes += dt;
          penalize(b, minuteNow, PASTEURIZE.SCORCH_PENALTY_PER_MIN * dt, 'Scorched above 150°F', 'quality', true);
        }
        if (!b.pasteurizeReady) {
          if (b.temp >= PASTEURIZE.BAND_LOW && b.temp <= PASTEURIZE.BAND_HIGH) {
            b.heldMinutes += dt;
            if (b.temp >= 144 && b.temp <= 146) b.perfectionWindows.pasteurize = (b.perfectionWindows.pasteurize ?? 0) + dt;
            else b.perfectionWindows.pasteurize = 0;
            if ((b.perfectionWindows.pasteurize ?? 0) >= PASTEURIZE.HOLD_MINUTES && !b.perfectedTasks.includes('pasteurize')) b.perfectedTasks.push('pasteurize');
            if (b.heldMinutes >= PASTEURIZE.HOLD_MINUTES) b.pasteurizeReady = true;
          } else b.perfectionWindows.pasteurize = 0;
        } else if (b.temp >= PASTEURIZE.OVERHOLD_TEMP) {
          b.overheldMinutes += dt;
          penalize(b, minuteNow, PASTEURIZE.OVERHOLD_PENALTY_PER_MIN * dt, 'Held hot past 30 minutes', 'quality', true);
        }
        break;
      }
      case 'heat': {
        if (b.temp > RICOTTA.SCORCH_TEMP) {
          b.scorchedMinutes += dt;
          penalize(b, minuteNow, RICOTTA.SCORCH_PENALTY_PER_MIN * dt, 'Whey scorched above 205°F', 'quality', true);
        }
        break;
      }
      case 'curdle': {
        if ((b.curdleReachedMinute ?? null) === null && b.temp <= CURDLE.BAND_HIGH) b.curdleReachedMinute = minuteNow;
        if (
          !b.acidAdded &&
          !b.curdleOvertimeApplied &&
          b.curdleReachedMinute !== null &&
          minuteNow - b.curdleReachedMinute > CURDLE.MAX_WAIT_MINUTES
        ) {
          b.curdleOvertimeApplied = true;
          penalize(b, minuteNow, CURDLE.OVERTIME_PENALTY, 'Cooled milk sat too long before the acid');
        }
        break;
      }
      case 'stretch': {
        if (b.heating && b.temp > STRETCH.OVERHEAT_TEMP && !b.overheatApplied) {
          b.overheatApplied = true;
          b.heating = false;
          penalize(b, minuteNow, STRETCH.OVERHEAT_PENALTY, 'Curd overheated past 180°F');
        }
        break;
      }
      case 'mold': {
        if (b.moldStartMinute !== null && !b.moldDone && minuteNow - b.moldStartMinute >= TALEGGIO.DRAIN_MINUTES) {
          b.moldDone = true;
          const windows = TALEGGIO.DRAIN_MINUTES / TALEGGIO.TURN_EVERY;
          let missed = 0;
          for (let w = 0; w < windows; w++) {
            const hit = b.moldTurns.some((t) => Math.floor(t / TALEGGIO.TURN_EVERY) === w);
            if (!hit) missed++;
          }
          if (missed > 0) penalize(b, minuteNow, TALEGGIO.MISSED_TURN_PENALTY * missed, `Molds not turned ×${missed}`);
        }
        break;
      }
      default:
        break;
    }
  }
}

function nextStage(b: Batch, now: number): void {
  const order = PROCESS_STAGES[b.process];
  const i = order.indexOf(b.stage);
  if (i >= 0 && i < order.length - 1) {
    b.stage = order[i + 1];
    b.stageStartMinute = now;
  }
}

// ---- Heating (pasteurize / ricotta heat) ---------------------------------

export function setBurner(b: Batch, burner: Burner): void {
  if (b.stage === 'pasteurize' || b.stage === 'heat') b.burner = burner;
}

export function pullPasteurized(b: Batch, now: number): boolean {
  if (b.stage !== 'pasteurize' || !b.pasteurizeReady) return false;
  b.burner = 'off';
  nextStage(b, now);
  return true;
}

export function setCooling(b: Batch, on: boolean): void {
  if (b.stage === 'curdle' || b.stage === 'culture') b.cooling = on;
}

// ---- Mozzarella ----------------------------------------------------------

export function addCitricAcid(b: Batch, now: number): boolean {
  if (b.stage !== 'curdle' || b.acidAdded) return false;
  b.acidAdded = true;
  b.acidTemp = b.temp;
  b.cooling = false;
  const below = CURDLE.BAND_LOW - b.temp;
  const above = b.temp - CURDLE.BAND_HIGH;
  const outside = Math.max(0, below, above);
  if (outside > 0) penalize(b, now, CURDLE.OUT_OF_BAND_PENALTY_PER_F * outside, `Acid added ${outside.toFixed(1)}°F outside 88–90°F`);
  return true;
}

export function finishCurdleStir(b: Batch, now: number, misses: number): boolean {
  if (b.stage !== 'curdle' || !b.acidAdded || b.curdleStirred) return false;
  b.curdleStirred = true;
  if (misses > 0) penalize(b, now, CURDLE.MISS_PENALTY * misses, `Off-tempo stirring ×${misses}`);
  if (misses === 0) b.perfectedTasks.push('curdle_stir');
  nextStage(b, now);
  return true;
}

export function addRennet(b: Batch): boolean {
  if (b.stage !== 'rennet' || b.rennetAdded) return false;
  b.rennetAdded = true;
  return true;
}

export function finishRennetStir(b: Batch, now: number): boolean {
  if (b.stage !== 'rennet' || !b.rennetAdded || b.rennetStirred) return false;
  b.rennetStirred = true;
  b.rennetAddedMinute = now;
  return true;
}

export function setMinutes(b: Batch, now: number): number {
  return b.rennetAddedMinute === null ? 0 : now - b.rennetAddedMinute;
}

export function setWindow(b: Batch): { min: number; max: number } {
  return b.process === 'taleggio' ? { min: TALEGGIO.SET_MIN, max: TALEGGIO.SET_MAX } : { min: RENNET.SET_MIN, max: RENNET.SET_MAX };
}

export function checkSet(b: Batch, now: number): boolean {
  if (b.stage !== 'rennet' || !b.rennetStirred) return false;
  const t = setMinutes(b, now);
  const w = setWindow(b);
  const earlyRate = b.process === 'taleggio' ? TALEGGIO.EARLY_YIELD_PENALTY_PER_MIN : RENNET.EARLY_YIELD_PENALTY_PER_MIN;
  const lateRate = b.process === 'taleggio' ? TALEGGIO.LATE_QUALITY_PENALTY_PER_MIN : RENNET.LATE_QUALITY_PENALTY_PER_MIN;
  if (t < w.min) penalize(b, now, earlyRate * (w.min - t), `Checked curd ${(w.min - t).toFixed(1)} min early`, 'yield');
  if (t > w.max) penalize(b, now, lateRate * (t - w.max), `Checked curd ${(t - w.max).toFixed(1)} min late`);
  nextStage(b, now);
  return true;
}

export function slice(b: Batch): boolean {
  const max = b.process === 'taleggio' ? TALEGGIO.SLICES : CUT.SLICES;
  if ((b.stage !== 'cut' && b.stage !== 'mold') || b.slices >= max) return false;
  b.slices++;
  return true;
}

/** Returns gallons of whey produced. */
export function drain(b: Batch): number {
  if (b.stage !== 'cut' || b.slices < CUT.SLICES || b.drained) return 0;
  b.drained = true;
  return Math.round(b.gallons * CUT.WHEY_PER_GALLON * 10) / 10;
}

export function finishFirming(b: Batch, now: number, earlyClicks: number): boolean {
  if (b.stage !== 'cut' || !b.drained || b.firmed) return false;
  b.firmed = true;
  if (earlyClicks > 0) penalize(b, now, CUT.EARLY_YIELD_PENALTY * earlyClicks, `Stirred too fast ×${earlyClicks}, broke curds`, 'yield');
  if (earlyClicks === 0) b.perfectedTasks.push('curd_harden');
  nextStage(b, now);
  b.heating = false;
  return true;
}

export function setHeating(b: Batch, on: boolean): void {
  if (b.stage === 'stretch') b.heating = on;
}

export function canStretch(b: Batch): boolean {
  return b.stage === 'stretch' && b.temp >= STRETCH.TARGET_TEMP - 5 && !b.stretched;
}

export function finishStretching(b: Batch, now: number, misses: number, perfectZone = false): boolean {
  if (!canStretch(b)) return false;
  b.stretched = true;
  b.heating = false;
  if (misses > 0) penalize(b, now, STRETCH.MISS_PENALTY * misses, `Torn stretches ×${misses}`);
  if (perfectZone && misses === 0) b.perfectedTasks.push('stretch');
  return true;
}

export function formBalls(b: Batch, now: number): number {
  if (b.stage !== 'stretch' || !b.stretched || b.formed) return 0;
  b.formed = true;
  b.units = projectedUnits(b);
  nextStage(b, now);
  return b.units;
}

// ---- Ricotta -------------------------------------------------------------

export function ricottaInBand(b: Batch): boolean {
  return b.temp >= RICOTTA.BAND_LOW && b.temp <= RICOTTA.BAND_HIGH;
}

export function addRicottaAcid(b: Batch, now: number): boolean {
  if (b.stage !== 'heat') return false;
  const below = RICOTTA.BAND_LOW - b.temp;
  const above = b.temp - RICOTTA.BAND_HIGH;
  const outside = Math.max(0, below, above);
  if (outside > 0) penalize(b, now, RICOTTA.OUT_OF_BAND_PENALTY_PER_F * outside, `Acid added ${outside.toFixed(1)}°F outside 190–200°F`);
  b.burner = 'off';
  b.acidAdded = true;
  b.acidTemp = b.temp;
  b.restStartMinute = now;
  nextStage(b, now);
  return true;
}

export function restMinutes(b: Batch, now: number): number {
  return b.restStartMinute === null ? 0 : now - b.restStartMinute;
}

/** Stop the rest and start skimming. Early loses curd, late toughens it. */
export function startSkim(b: Batch, now: number): boolean {
  if (b.stage !== 'acidify') return false;
  const t = restMinutes(b, now);
  if (t < RICOTTA.REST_MIN) penalize(b, now, RICOTTA.EARLY_REST_YIELD_PER_MIN * (RICOTTA.REST_MIN - t), `Skimmed ${(RICOTTA.REST_MIN - t).toFixed(1)} min early`, 'yield');
  if (t > RICOTTA.REST_MAX) penalize(b, now, RICOTTA.LATE_REST_QUALITY_PER_MIN * (t - RICOTTA.REST_MAX), `Rested ${(t - RICOTTA.REST_MAX).toFixed(1)} min too long`);
  nextStage(b, now);
  return true;
}

export function finishSkim(b: Batch, now: number, misses: number): boolean {
  if (b.stage !== 'skim' || b.skimmed) return false;
  b.skimmed = true;
  if (misses > 0) penalize(b, now, RICOTTA.SKIM_MISS_YIELD * misses, `Rough scoops ×${misses}`, 'yield');
  b.drainStartMinute = now;
  nextStage(b, now);
  return true;
}

export function drainMinutes(b: Batch, now: number): number {
  return b.drainStartMinute === null ? 0 : now - b.drainStartMinute;
}

export function canTub(b: Batch, now: number): boolean {
  return b.stage === 'drain' && drainMinutes(b, now) >= RICOTTA.DRAIN_MIN;
}

export function finishDrain(b: Batch, now: number): number {
  if (!canTub(b, now)) return 0;
  b.units = projectedUnits(b);
  nextStage(b, now);
  return b.units;
}

// ---- Taleggio ------------------------------------------------------------

export function cultureInBand(b: Batch): boolean {
  return b.temp >= TALEGGIO.CULTURE_BAND_LOW && b.temp <= TALEGGIO.CULTURE_BAND_HIGH;
}

export function addCulture(b: Batch, now: number): boolean {
  if (b.stage !== 'culture' || b.cultureAdded) return false;
  b.cultureAdded = true;
  b.cultureTemp = b.temp;
  b.cultureAddedMinute = now;
  b.cooling = false;
  const below = TALEGGIO.CULTURE_BAND_LOW - b.temp;
  const above = b.temp - TALEGGIO.CULTURE_BAND_HIGH;
  const outside = Math.max(0, below, above);
  if (outside > 0) penalize(b, now, TALEGGIO.OUT_OF_BAND_PENALTY_PER_F * outside, `Culture added ${outside.toFixed(1)}°F outside 90–93°F`);
  return true;
}

export function ripenMinutes(b: Batch, now: number): number {
  return b.cultureAddedMinute === null ? 0 : now - b.cultureAddedMinute;
}

export function checkRipening(b: Batch, now: number): boolean {
  if (b.stage !== 'culture' || !b.cultureAdded || b.ripened) return false;
  const t = ripenMinutes(b, now);
  if (t < TALEGGIO.RIPEN_MIN) penalize(b, now, TALEGGIO.RIPEN_EARLY_PENALTY_PER_MIN * (TALEGGIO.RIPEN_MIN - t), `Ripening cut ${(TALEGGIO.RIPEN_MIN - t).toFixed(1)} min short`);
  if (t > TALEGGIO.RIPEN_MAX) penalize(b, now, TALEGGIO.RIPEN_LATE_PENALTY_PER_MIN * (t - TALEGGIO.RIPEN_MAX), `Ripened ${(t - TALEGGIO.RIPEN_MAX).toFixed(1)} min too long`);
  b.ripened = true;
  nextStage(b, now);
  return true;
}

/** The set curd is carried from the vat to the draining table. */
export function carryToTable(b: Batch, now: number): boolean {
  if (b.process !== 'taleggio' || b.stage !== 'rennet' || !b.rennetStirred) return false;
  return checkSet(b, now);
}

/** Ladle the cut curd into the square molds. Returns whey gallons. */
export function fillMolds(b: Batch, now: number): number {
  if (b.stage !== 'mold' || b.slices < TALEGGIO.SLICES || b.moldStartMinute !== null) return 0;
  b.moldStartMinute = now;
  b.drained = true;
  return Math.round(b.gallons * TALEGGIO.WHEY_PER_GALLON * 10) / 10;
}

export function moldMinutes(b: Batch, now: number): number {
  return b.moldStartMinute === null ? 0 : now - b.moldStartMinute;
}

export function turnMolds(b: Batch, now: number): boolean {
  if (b.stage !== 'mold' || b.moldStartMinute === null || b.moldDone) return false;
  const t = moldMinutes(b, now);
  const window = Math.floor(t / TALEGGIO.TURN_EVERY);
  if (b.moldTurns.some((m) => Math.floor(m / TALEGGIO.TURN_EVERY) === window)) return false;
  b.moldTurns.push(t);
  return true;
}

export function carryToTub(b: Batch, now: number): boolean {
  if (b.stage !== 'mold' || !b.moldDone) return false;
  nextStage(b, now);
  return true;
}

export function submerge(b: Batch, now: number): boolean {
  if (b.stage !== 'brine' || b.brineStartMinute !== null) return false;
  b.brineStartMinute = now;
  return true;
}

export function brineHours(b: Batch, now: number): number {
  return b.brineStartMinute === null ? 0 : (now - b.brineStartMinute) / 60;
}

export function pullFromBrine(b: Batch, now: number): number {
  if (b.stage !== 'brine' || b.brineStartMinute === null) return 0;
  const h = brineHours(b, now);
  if (h < TALEGGIO.BRINE_MIN_HOURS) penalize(b, now, TALEGGIO.UNDER_BRINE_PENALTY_PER_HOUR * (TALEGGIO.BRINE_MIN_HOURS - h), `Under-brined by ${(TALEGGIO.BRINE_MIN_HOURS - h).toFixed(1)} h`);
  if (h > TALEGGIO.BRINE_MAX_HOURS) penalize(b, now, TALEGGIO.OVER_BRINE_PENALTY_PER_HOUR * (h - TALEGGIO.BRINE_MAX_HOURS), `Over-brined by ${(h - TALEGGIO.BRINE_MAX_HOURS).toFixed(1)} h`);
  b.units = projectedUnits(b);
  nextStage(b, now);
  return b.units;
}

export const STAGE_ORDER: Stage[] = PROCESS_STAGES.mozzarella;
